import { ExtractComponentProp, FormValues } from '@approvalmax/types';
import { Form, Grid, Select } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { getStandaloneFields } from 'pages/requestForm/selectors/standalone';
import { memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';

import { changeStandaloneFieldValue } from '../../../actions';
import { getShowValidationErrors } from '../../../selectors/pageSelectors';
import { messages } from './StandaloneFieldsSection.messages';
import { useSchema } from './StandaloneFieldsSection.schema';

export const StandaloneFieldsSection = memo(() => {
    const dispatch = useDispatch();
    const showErrors = useSelector(getShowValidationErrors);
    const fieldValues = useSelector(getStandaloneFields);

    const { schema } = useSchema(fieldValues);

    const values = fieldValues.reduce<Record<string, string | null>>((acc, fieldValue) => {
        acc[fieldValue.field.id] = fieldValue.value?.id || null;

        return acc;
    }, {});

    const form = useForm<z.infer<typeof schema>>({
        values,
        mode: 'onChange',
        resetOptions: {
            keepErrors: true,
        },
        resolver: zodResolver(schema),
    });

    const handleChange: ExtractComponentProp<typeof Form<FormValues<typeof form>>, 'onChange'> = (values, fieldId) => {
        const fieldValue = fieldValues.find((fieldValue) => fieldValue.field.id === fieldId);
        const value = fieldValue?.field.exactValues.find((value) => value.id === values[fieldId]);

        dispatch(changeStandaloneFieldValue(fieldId, value || null));
    };

    useEffect(() => {
        showErrors && form.trigger();
    }, [showErrors, form]);

    if (!fieldValues.length) return null;

    return (
        <Form form={form} onChange={handleChange}>
            <Grid gridTemplateColumns='repeat(auto-fill, 220px)' gap={16}>
                {fieldValues.map((fieldValue) => (
                    <Select
                        name={fieldValue.field.id}
                        label={fieldValue.field.name}
                        placeholder={messages.standaloneFieldPlaceholder}
                        items={fieldValue.field.exactValues}
                        itemNameKey='text'
                        required
                        size='small'
                        qa={fieldValue.field.name}
                        key={fieldValue.field.id}
                    />
                ))}
            </Grid>
        </Form>
    );
});

StandaloneFieldsSection.displayName = 'StandaloneFieldsSection';
